import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useAsyncEffect from '../../../packages/hooks/src/useAsyncEffect';
import useProviderId from './useProviderId';
import type { IRootDispatch, RootState } from '../store/models';
import store from '../store';
import { PublicProvider, PrivilegedProvider } from '../api/Providers';

function useActiveProvider(needsAdmin: true): PrivilegedProvider;

function useActiveProvider(): PublicProvider;

function useActiveProvider(needsAdmin: false): PublicProvider;

/**
 * useActiveProvider - hook to get cached
 * activeProvider from providers model
 * or if it isn't cached, load and
 * return it async.
 *
 * @see SearchRank page to see it in use
 *
 * @returns {PublicProvider} provider
 */
function useActiveProvider(needsAdmin?: boolean): PublicProvider | PrivilegedProvider {
  const [provider, setProvider] = useState<PublicProvider | PrivilegedProvider>(null);
  const pro = useSelector<
  RootState,
  PublicProvider | PrivilegedProvider
  >(store.select.providers.activeProvider);
  const dispatch = useDispatch<IRootDispatch>();
  const providerId = useProviderId();

  useAsyncEffect(async () => {
    if (pro && pro?.id === providerId) {
      setProvider(pro);
    } else if (providerId > 0) {
      const fetchedPro = await dispatch.providers.loadProvider({
        providerId,
        setActive: needsAdmin,
      });
      setProvider(fetchedPro);
    }
  }, [pro]);

  if (needsAdmin) {
    return provider as PrivilegedProvider;
  }

  return provider;
}

export default useActiveProvider;
